import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Skeleton } from "@/shared/ui";
import type { ProfitLossChartPoint } from "../../../types/api";
import { formatCompact } from "../../../utils/format";
import ChartLegend from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

interface Props {
  data: ProfitLossChartPoint[];
  isLoading: boolean;
}

const COLOR_REVENUE = "#11BF68";
const COLOR_EXPENSE = "#94A3B8";

export default function ProfitLossChart({ data, isLoading }: Props) {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = i18n.language || "ru";

  if (isLoading) {
    return <Skeleton height={240} className="radius-sm" />;
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[240px] text-body-regular-sm content-base-secondary">
        {t("page.noData")}
      </div>
    );
  }

  function tickFormatter(value: number) {
    return formatCompact(value, locale);
  }

  return (
    <div style={{ height: 240 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }} barGap={data.length === 1 ? -32 : 4}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={tickFormatter}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "transparent" }} />
          <Legend content={<ChartLegend />} />
          <Bar
            dataKey="revenue"
            name={t("profitLoss.revenue")}
            fill={COLOR_REVENUE}
            fillOpacity={0.85}
            activeBar={{ fillOpacity: 1 }}
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          />
          <Bar
            dataKey="expense"
            name={t("profitLoss.expense")}
            fill={COLOR_EXPENSE}
            fillOpacity={0.85}
            activeBar={{ fillOpacity: 1 }}
            radius={[4, 4, 0, 0]}
            maxBarSize={32}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
